import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Platform,
  ScrollView,
  Dimensions,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '../contexts/ThemeContext';
import { getColors } from '../styles/theme';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const IS_MOBILE = SCREEN_WIDTH < 768;

// Tag groupings for organized display
const TAG_GROUPS = [
  {
    label: 'Venue Type',
    tags: ['coffee-shop', 'bar', 'library', 'park'],
  },
  {
    label: 'Setting',
    tags: ['indoor', 'outdoor'],
  },
  {
    label: 'Play Style',
    tags: ['casual', 'competitive', 'tournament', 'blitz', 'rapid', 'classical', 'rated-games'],
  },
  {
    label: 'Player Level',
    tags: ['beginner-friendly', 'advanced', 'youth', 'adult'],
  },
];

const RADIUS_OPTIONS = [
  { value: 5, label: '5 km' },
  { value: 10, label: '10 km' },
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
  { value: null, label: 'Any' },
];

const formatTag = (tag) => tag.replace(/-/g, ' ');

const MapFilterModal = ({ visible, onClose, onApplyFilters, initialFilters = {} }) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);

  // Filter state
  const [selectedTags, setSelectedTags] = useState(initialFilters.tags || []);
  const [tagsMatchMode, setTagsMatchMode] = useState(initialFilters.tagsMatchMode || 'any');
  const [activityLevel, setActivityLevel] = useState(initialFilters.activityLevel || null);
  const [clubTier, setClubTier] = useState(initialFilters.tier || null);
  const [hasSchedule, setHasSchedule] = useState(initialFilters.hasSchedule ?? null);
  const [radius, setRadius] = useState(initialFilters.radius ?? null);
  const [hasSocialLink, setHasSocialLink] = useState(initialFilters.hasSocialLink ?? null);

  // Reset filters when modal opens
  useEffect(() => {
    if (visible) {
      setSelectedTags(initialFilters.tags || []);
      setTagsMatchMode(initialFilters.tagsMatchMode || 'any');
      setActivityLevel(initialFilters.activityLevel || null);
      setClubTier(initialFilters.tier || null);
      setHasSchedule(initialFilters.hasSchedule ?? null);
      setRadius(initialFilters.radius ?? null);
      setHasSocialLink(initialFilters.hasSocialLink ?? null);
    }
  }, [visible, initialFilters]);

  const handleApply = () => {
    const filters = {
      tags: selectedTags,
      tagsMatchMode,
      activityLevel,
      tier: clubTier,
      hasSchedule,
      radius,
      hasSocialLink,
    };
    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    setSelectedTags([]);
    setTagsMatchMode('any');
    setActivityLevel(null);
    setClubTier(null);
    setHasSchedule(null);
    setRadius(null);
    setHasSocialLink(null);
  };

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const hasActiveFilters = () => {
    return (
      selectedTags.length > 0 ||
      activityLevel !== null ||
      clubTier !== null ||
      hasSchedule !== null ||
      radius !== null ||
      hasSocialLink !== null
    );
  };

  const activeFilterCount = () => {
    let count = 0;
    if (selectedTags.length > 0) count++;
    if (activityLevel !== null) count++;
    if (clubTier !== null) count++;
    if (hasSchedule !== null) count++;
    if (radius !== null) count++;
    if (hasSocialLink !== null) count++;
    return count;
  };

  if (!visible) return null;

  const chipBg = isDarkMode ? '#2A2A2A' : '#F0F0F0';
  const chipBorder = isDarkMode ? '#3A3A3A' : '#E0E0E0';
  const accentColor = '#84D288';

  const renderChip = (label, isSelected, onPress, style) => (
    <TouchableOpacity
      key={label}
      style={[
        styles.chip,
        {
          backgroundColor: isSelected ? accentColor : chipBg,
          borderColor: isSelected ? accentColor : chipBorder,
        },
        style,
      ]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text
        style={[
          styles.chipText,
          {
            color: isSelected ? '#fff' : colors.text,
            fontWeight: isSelected ? '600' : '400',
          },
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );

  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View
          style={[
            styles.modal,
            {
              backgroundColor: colors.surface,
              ...(IS_MOBILE ? styles.modalMobile : styles.modalDesktop),
            },
          ]}
        >
          {/* ── Header ── */}
          <View style={[styles.header, { borderBottomColor: colors.border }]}>
            <View style={styles.headerLeft}>
              <Text style={[styles.headerTitle, { color: colors.text }]}>Filters</Text>
              {hasActiveFilters() && (
                <View style={styles.headerBadge}>
                  <Text style={styles.headerBadgeText}>{activeFilterCount()}</Text>
                </View>
              )}
            </View>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
              <Icon name="close" size={22} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* ── Scrollable Content ── */}
          <ScrollView
            style={styles.scrollBody}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            bounces={false}
          >
            {/* ── Radius ── */}
            <View style={styles.section}>
              <Text style={[styles.sectionLabel, { color: colors.text }]}>Search Radius</Text>
              <View style={styles.chipRow}>
                {RADIUS_OPTIONS.map((opt) =>
                  renderChip(opt.label, radius === opt.value, () =>
                    setRadius(radius === opt.value ? null : opt.value),
                  ),
                )}
              </View>
            </View>

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            {/* ── Venue has social (hotspots) ── */}
            <View style={styles.section}>
              <Text style={[styles.sectionLabel, { color: colors.text }]}>Venue has social</Text>
              <View style={styles.chipRow}>
                {[
                  { value: null, label: 'Any' },
                  { value: 'instagram', label: 'Instagram' },
                  { value: 'twitter', label: 'Twitter / X' },
                  { value: 'facebook', label: 'Facebook' },
                ].map((opt) =>
                  renderChip(opt.label, hasSocialLink === opt.value, () =>
                    setHasSocialLink(hasSocialLink === opt.value ? null : opt.value),
                  ),
                )}
              </View>
            </View>

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            {/* ── Tags ── */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={[styles.sectionLabel, { color: colors.text }]}>Tags</Text>
                {/* Match mode toggle */}
                <View style={[styles.matchToggle, { backgroundColor: chipBg, borderColor: chipBorder }]}>
                  <TouchableOpacity
                    style={[
                      styles.matchToggleBtn,
                      tagsMatchMode === 'any' && { backgroundColor: accentColor },
                    ]}
                    onPress={() => setTagsMatchMode('any')}
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[
                        styles.matchToggleText,
                        { color: tagsMatchMode === 'any' ? '#fff' : colors.textSecondary },
                      ]}
                    >
                      Any
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.matchToggleBtn,
                      tagsMatchMode === 'all' && { backgroundColor: accentColor },
                    ]}
                    onPress={() => setTagsMatchMode('all')}
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[
                        styles.matchToggleText,
                        { color: tagsMatchMode === 'all' ? '#fff' : colors.textSecondary },
                      ]}
                    >
                      All
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>

              {TAG_GROUPS.map((group) => (
                <View key={group.label} style={styles.tagGroup}>
                  <Text style={[styles.tagGroupLabel, { color: colors.textSecondary }]}>
                    {group.label}
                  </Text>
                  <View style={styles.chipRow}>
                    {group.tags.map((tag) =>
                      renderChip(formatTag(tag), selectedTags.includes(tag), () => toggleTag(tag)),
                    )}
                  </View>
                </View>
              ))}
            </View>

            <View style={[styles.divider, { backgroundColor: colors.border }]} />

            {/* ── Activity Level ── */}
            <View style={styles.section}>
              <Text style={[styles.sectionLabel, { color: colors.text }]}>Activity Level</Text>
              <View style={styles.chipRow}>
                {['HIGH', 'MEDIUM', 'LOW'].map((level) =>
                  renderChip(
                    level.charAt(0) + level.slice(1).toLowerCase(),
                    activityLevel === level,
                    () => setActivityLevel(activityLevel === level ? null : level),
                  ),
                )}
              </View>
            </View>

            {/* ── Club Tier & Schedule (side by side on desktop) ── */}
            <View style={IS_MOBILE ? styles.section : styles.rowSection}>
              <View style={IS_MOBILE ? null : styles.halfSection}>
                <Text style={[styles.sectionLabel, { color: colors.text }]}>Club Tier</Text>
                <View style={styles.chipRow}>
                  {['FREE', 'PRO'].map((tier) =>
                    renderChip(tier, clubTier === tier, () =>
                      setClubTier(clubTier === tier ? null : tier),
                    ),
                  )}
                </View>
              </View>
              {IS_MOBILE && <View style={{ height: 16 }} />}
              <View style={IS_MOBILE ? null : styles.halfSection}>
                <Text style={[styles.sectionLabel, { color: colors.text }]}>Has Schedule</Text>
                <View style={styles.chipRow}>
                  {renderChip('Yes', hasSchedule === true, () =>
                    setHasSchedule(hasSchedule === true ? null : true),
                  )}
                  {renderChip('No', hasSchedule === false, () =>
                    setHasSchedule(hasSchedule === false ? null : false),
                  )}
                </View>
              </View>
            </View>
          </ScrollView>

          {/* ── Footer ── */}
          <View style={[styles.footer, { borderTopColor: colors.border }]}>
            <TouchableOpacity
              style={[styles.resetBtn, { borderColor: colors.border }]}
              onPress={handleReset}
              activeOpacity={0.7}
            >
              <Icon name="refresh" size={16} color={colors.textSecondary} style={{ marginRight: 4 }} />
              <Text style={[styles.resetBtnText, { color: colors.textSecondary }]}>Reset</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.applyBtn} onPress={handleApply} activeOpacity={0.7}>
              <Text style={styles.applyBtnText}>
                {hasActiveFilters() ? 'Apply Filters' : 'Clear Filters'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

/* ──────────────────────────── Styles ──────────────────────────── */

const styles = StyleSheet.create({
  /* Overlay */
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000 }
      : {}),
  },

  /* Modal shell */
  modal: {
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 24,
  },
  modalMobile: {
    width: '94%',
    maxWidth: 440,
    maxHeight: '85%',
  },
  modalDesktop: {
    width: 460,
    maxHeight: '75%',
  },

  /* Header */
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  headerBadge: {
    backgroundColor: '#84D288',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  headerBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
  },

  /* Scroll body */
  scrollBody: {
    flexShrink: 1,
  },
  scrollContent: {
    paddingVertical: 8,
  },

  /* Section */
  section: {
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginHorizontal: 20,
  },

  /* Side-by-side on desktop */
  rowSection: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 12,
    gap: 20,
  },
  halfSection: {
    flex: 1,
  },

  /* Tags */
  tagGroup: {
    marginBottom: 10,
  },
  tagGroupLabel: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
    marginTop: 4,
  },

  /* Chip (shared for tags, options, radius) */
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
  },
  chipText: {
    fontSize: 13,
    textTransform: 'capitalize',
  },

  /* Match mode toggle */
  matchToggle: {
    flexDirection: 'row',
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    marginBottom: 8,
  },
  matchToggleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  matchToggleText: {
    fontSize: 12,
    fontWeight: '600',
  },

  /* Footer */
  footer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: 10,
  },
  resetBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
  },
  resetBtnText: {
    fontSize: 14,
    fontWeight: '600',
  },
  applyBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#84D288',
    alignItems: 'center',
    justifyContent: 'center',
  },
  applyBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#fff',
  },
});

export default MapFilterModal;
